import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  List,
  ListItemButton,
  Popover,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { axiosInstance } from "../../../utils/networking";
import findObjectDifferences from "../../../utils/utils";

const BRAND = "#9E1F63";

const initialFormData = {
  s1_stage: "",
  s1_notes: "",

  s2_doc_checks: [] as string[],
  s2_client_status: [] as string[],
  s2_required_docs: [] as string[],
  s2_notes: "",

  s3_target_audience: "",
  s3_uvp: "",
  s3_marketing_plan: "",
  s3_channels: [] as string[],
  s3_social_platforms: "",
  s3_website_updated: "",
  s3_brand_consistent: "",
  s3_testimonials: "",
  s3_pillar: [] as string[],
  s3_notes: "",

  s4_rev_current: "",
  s4_rev_2025: "",
  s4_rev_2024: "",
  s4_software: "",
  s4_bookkeeper: "",
  s4_separate_finances: "",
  s4_cash_flow: "",
  s4_pillar: [] as string[],
  s4_notes: "",

  s5_contracts: "",
  s5_terms: "",
  s5_insurance: "",
  s5_pillar: [] as string[],
  s5_notes: "",

  s6_email_platform: "",
  s6_file_storage: "",
  s6_crm: "",
  s6_cybersecurity: "",
  s6_data_backup: "",
  s6_pillar: [] as string[],
  s6_notes: "",

  s7_kpis: "",
  s7_sales_freq: "",
  s7_mktg_analytics: "",
  s7_tools: "",
  s7_data_basis: "",
  s7_pillar: [] as string[],
  s7_notes: "",

  s8_emp_current: "",
  s8_emp_2025: "",
  s8_emp_2024: "",
  s8_pillar: [] as string[],
  s8_notes: "",

  s9_priority_1: "",
  s9_priority_2: "",
  s9_priority_3: "",
  s9_notes: "",

  s10_key_risks: "",
  s10_strengths: "",
  s10_immediate_gaps: "",
  s10_growth_opps: "",
  s10_quick_wins: "",
  s10_mid_term: "",
  s10_long_term: "",
  s10_support_areas: [] as string[],
  s10_client_deliverables: "",
  s10_next_meeting: "",
  s10_meeting_obj: "",
  s10_responsibilities: "",
  s10_referrals: "",
  s10_b2b_accomplish: "",
  s10_b2b_hours: "",
  s10_notes: "",
};

type FormData = typeof initialFormData;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const compareByDate = (a: any, b: any) =>
  new Date(b.created_at).getTime() - new Date(a.created_at).getTime();

const getSurveyResultId = (activity: any[]) => {
  const bna = activity
    .filter((a: any) => a.activity_type === "Business Assessment")
    .sort(compareByDate);
  return bna.length > 0 ? bna[0]["survey_result_id"] : null;
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function SectionHeader({ title, purpose }: { title: string; purpose: string }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="h6" sx={{ color: BRAND, fontWeight: 700, lineHeight: 1.3 }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic", mt: 0.25 }}>
        Purpose: {purpose}
      </Typography>
    </Box>
  );
}

function DiscussionBox({ prompts }: { prompts: string[] }) {
  return (
    <Box
      sx={{
        bgcolor: "#fdf4f8",
        borderLeft: `4px solid ${BRAND}`,
        p: 1.5,
        mb: 2,
        borderRadius: "0 6px 6px 0",
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          color: BRAND,
          display: "block",
          mb: 0.75,
          textTransform: "uppercase",
          letterSpacing: 0.5,
        }}
      >
        Discussion Prompts
      </Typography>
      {prompts.map((p, i) => (
        <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
          • {p}
        </Typography>
      ))}
    </Box>
  );
}

function YesNo({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <FormControl sx={{ mb: 1.5, display: "block" }}>
      <FormLabel sx={{ fontSize: "0.875rem", mb: 0.25 }}>{label}</FormLabel>
      <RadioGroup row value={value} onChange={(e) => onChange(e.target.value)}>
        <FormControlLabel value="Yes" control={<Radio size="small" />} label="Yes" />
        <FormControlLabel value="No" control={<Radio size="small" />} label="No" />
      </RadioGroup>
    </FormControl>
  );
}

function YesNoSomewhat({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <FormControl sx={{ mb: 1.5, display: "block" }}>
      <FormLabel sx={{ fontSize: "0.875rem", mb: 0.25 }}>{label}</FormLabel>
      <RadioGroup row value={value} onChange={(e) => onChange(e.target.value)}>
        <FormControlLabel value="Yes" control={<Radio size="small" />} label="Yes" />
        <FormControlLabel value="Somewhat" control={<Radio size="small" />} label="Somewhat" />
        <FormControlLabel value="No" control={<Radio size="small" />} label="No" />
      </RadioGroup>
    </FormControl>
  );
}

function CheckboxGroup({
  label,
  options,
  values,
  onChange,
}: {
  label?: string;
  options: string[];
  values: string[];
  onChange: (v: string, checked: boolean) => void;
}) {
  return (
    <FormControl component="fieldset" sx={{ mb: 2, display: "block" }}>
      {label && (
        <FormLabel
          component="legend"
          sx={{ fontSize: "0.875rem", fontWeight: 600, mb: 0.5, color: "text.primary" }}
        >
          {label}
        </FormLabel>
      )}
      <FormGroup>
        {options.map((opt) => (
          <FormControlLabel
            key={opt}
            control={
              <Checkbox
                size="small"
                checked={values.includes(opt)}
                onChange={(e) => onChange(opt, e.target.checked)}
                sx={{ color: BRAND, "&.Mui-checked": { color: BRAND } }}
              />
            }
            label={<Typography variant="body2">{opt}</Typography>}
          />
        ))}
      </FormGroup>
    </FormControl>
  );
}

function NotesArea({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <Box sx={{ mt: 2 }}>
      <Divider sx={{ mb: 2 }} />
      <TextField
        fullWidth
        multiline
        minRows={4}
        label="Notes"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        variant="outlined"
        size="small"
      />
    </Box>
  );
}

// ─── Tab panels ───────────────────────────────────────────────────────────────

type TabProps = {
  data: FormData;
  set: (f: string, v: any) => void;
  cb: (f: string, v: string, c: boolean) => void;
};

function Tab1({ data, set }: TabProps) {
  return (
    <Box sx={{ p: 2 }}>
      <SectionHeader
        title="Business Snapshot – Where the Business Is Today"
        purpose="Establish baseline reality before jumping to solutions."
      />
      <FormControl component="fieldset" sx={{ mb: 2 }}>
        <FormLabel component="legend" sx={{ fontWeight: 600 }}>
          Business Stage
        </FormLabel>
        <RadioGroup value={data.s1_stage} onChange={(e) => set("s1_stage", e.target.value)}>
          {[
            "Plan and Launch (Startup Phase)",
            "Growth and Operations (Expansion)",
            "Maturation and Management (Stability)",
            "Transformation (Exit or Re-invention)",
          ].map((opt) => (
            <FormControlLabel key={opt} value={opt} control={<Radio size="small" />} label={opt} />
          ))}
        </RadioGroup>
      </FormControl>
      <DiscussionBox
        prompts={[
          "How would you describe your business in one sentence today?",
          "What problem do you solve, and for whom?",
          'What does a "good month" look like right now?',
          "What feels hardest about running the business today?",
        ]}
      />
      <NotesArea value={data.s1_notes} onChange={(v) => set("s1_notes", v)} />
    </Box>
  );
}

function Tab2({ data, set, cb }: TabProps) {
  return (
    <Box sx={{ p: 2 }}>
      <SectionHeader
        title="Business Identity & Compliance"
        purpose="Confirm that the business is properly formed and documented."
      />
      <DiscussionBox
        prompts={["Have you had any issues with getting licenses, renewals, or compliance?"]}
      />

      <CheckboxGroup
        label="Documentation Check"
        options={[
          "Legal business name",
          "DBA (if applicable)",
          "LARA ID (Michigan)",
          "Federal EIN",
          "Local licenses or permits",
          "Registered agent on file",
          "Business bank account established",
        ]}
        values={data.s2_doc_checks}
        onChange={(v, c) => cb("s2_doc_checks", v, c)}
      />

      <CheckboxGroup
        label="Client Status & Profile"
        options={[
          "Client is an active Oakland Thrive client currently working with an SBC",
          "Thrive Drive profile updated (name, email, phone, address, website, industry, all required fields)",
          "Confirm that no duplicate profiles exist for the same business/entity",
          "Business is registered with LARA at an Oakland County address OR proof of operation in Oakland County is uploaded",
        ]}
        values={data.s2_client_status}
        onChange={(v, c) => cb("s2_client_status", v, c)}
      />

      <CheckboxGroup
        label="Required Documentation"
        options={[
          "Needs Assessment completed and verified by Manager",
          "Draft business plan uploaded",
          "Notes included for any incomplete business plan sections (why incomplete + what is needed)",
          "Client has attended at least 3 OT workshops (in-person or virtual)",
          "SBC has identified client's urgent needs and recorded them in Thrive Drive",
          "There is a clear path forward using OT resources (B2B is one of several options)",
        ]}
        values={data.s2_required_docs}
        onChange={(v, c) => cb("s2_required_docs", v, c)}
      />

      <NotesArea value={data.s2_notes} onChange={(v) => set("s2_notes", v)} />
    </Box>
  );
}

function Tab3({ data, set, cb }: TabProps) {
  return (
    <Box sx={{ p: 2 }}>
      <SectionHeader
        title="Marketing (Strategy + Framework)"
        purpose="Understand positioning, strategy, and target audience."
      />

      <DiscussionBox
        prompts={[
          "Who is your ideal customer?",
          "What do you do better than your competitors?",
          "Who are your top competitors?",
          "What are your short-term and long-term growth goals?",
          'How do you currently define "marketing success"?',
          "Does your brand clearly communicate your value?",
        ]}
      />

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Clarity Check
      </Typography>
      <YesNoSomewhat
        label="Target audience clearly defined?"
        value={data.s3_target_audience}
        onChange={(v) => set("s3_target_audience", v)}
      />
      <YesNoSomewhat
        label="Unique value proposition articulated?"
        value={data.s3_uvp}
        onChange={(v) => set("s3_uvp", v)}
      />
      <YesNo
        label="Written marketing plan?"
        value={data.s3_marketing_plan}
        onChange={(v) => set("s3_marketing_plan", v)}
      />

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Marketing Execution — Current Channels in Use
      </Typography>
      <CheckboxGroup
        options={[
          "Website",
          "Social media",
          "Email marketing",
          "Paid ads",
          "Networking / referrals",
          "SEO / local listings",
        ]}
        values={data.s3_channels}
        onChange={(v, c) => cb("s3_channels", v, c)}
      />
      {data.s3_channels.includes("Social media") && (
        <TextField
          label="Which social platforms?"
          size="small"
          fullWidth
          value={data.s3_social_platforms}
          onChange={(e) => set("s3_social_platforms", e.target.value)}
          sx={{ mb: 2 }}
        />
      )}

      <DiscussionBox
        prompts={[
          "Which marketing activities bring the best results?",
          "What feels like a waste of time or money?",
          "Who handles marketing today? (Owner, staff, agency, no one)",
          "Is messaging consistent across platforms?",
        ]}
      />

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Assets Review
      </Typography>
      <YesNo
        label="Website up to date?"
        value={data.s3_website_updated}
        onChange={(v) => set("s3_website_updated", v)}
      />
      <YesNo
        label="Brand visuals consistent?"
        value={data.s3_brand_consistent}
        onChange={(v) => set("s3_brand_consistent", v)}
      />
      <YesNo
        label="Customer testimonials collected?"
        value={data.s3_testimonials}
        onChange={(v) => set("s3_testimonials", v)}
      />

      <CheckboxGroup
        label="Marketing Pillar Checklist — Client must have:"
        options={[
          "Brand colors + overall brand feel identified",
          "Ideal client identified",
          "Preliminary logo created (AI draft acceptable)",
          "Mission statement drafted and validated",
          "Professional photos (products, owner headshot, team)",
          "Website with About section developed",
          "At least one social media channel set up",
          "Google Business Profile claimed + optimized",
          "Passwords/logins organized and accessible for B2B provider",
        ]}
        values={data.s3_pillar}
        onChange={(v, c) => cb("s3_pillar", v, c)}
      />

      <NotesArea value={data.s3_notes} onChange={(v) => set("s3_notes", v)} />
    </Box>
  );
}

function Tab4({ data, set, cb }: TabProps) {
  return (
    <Box sx={{ p: 2 }}>
      <SectionHeader
        title="Accounting & Financial Management"
        purpose="Ensure financial clarity and sustainability."
      />

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Operational Snapshot
      </Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          label="Current Year Revenue"
          size="small"
          value={data.s4_rev_current}
          onChange={(e) => set("s4_rev_current", e.target.value)}
        />
        <TextField
          label="Revenue 2025"
          size="small"
          value={data.s4_rev_2025}
          onChange={(e) => set("s4_rev_2025", e.target.value)}
        />
        <TextField
          label="Revenue 2024"
          size="small"
          value={data.s4_rev_2024}
          onChange={(e) => set("s4_rev_2024", e.target.value)}
        />
      </Stack>

      <DiscussionBox
        prompts={[
          "How do you track income and expenses?",
          "Do you review financials monthly?",
          "Do you understand what you are reading?",
          "What are your main revenue streams?",
          "Do you understand the bookkeeping process?",
          "What are your tax preparation processes?",
          "Is your business profitable?",
        ]}
      />

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Systems & Support
      </Typography>
      <TextField
        label="Accounting software used"
        size="small"
        fullWidth
        value={data.s4_software}
        onChange={(e) => set("s4_software", e.target.value)}
        sx={{ mb: 2 }}
      />
      <YesNo
        label="Bookkeeper or accountant?"
        value={data.s4_bookkeeper}
        onChange={(v) => set("s4_bookkeeper", v)}
      />
      <YesNo
        label="Separate business/personal finances?"
        value={data.s4_separate_finances}
        onChange={(v) => set("s4_separate_finances", v)}
      />
      <YesNoSomewhat
        label="Cash flow predictable?"
        value={data.s4_cash_flow}
        onChange={(v) => set("s4_cash_flow", v)}
      />

      <CheckboxGroup
        label="Accounting Pillar Checklist — SBC must confirm or collect:"
        options={[
          "LARA Good Standing checked; status documented",
          "Legal entity type identified",
          "If not registered, referral to tax pro considered",
          "Receipts organized (binder, folder, etc.)",
          "Bookkeeping/accounting software, if used",
          "Login information to any financial systems",
          "Tax documents/returns (if any)",
          "Expense records",
          "Payroll records",
          "Loan/lease documents",
          "Financial statements (P&L, balance sheet, cash flow, equity)",
          "IRS/State/local notices (if any)",
          "Client prepared for financial discussion; trauma-informed approach used",
        ]}
        values={data.s4_pillar}
        onChange={(v, c) => cb("s4_pillar", v, c)}
      />

      <NotesArea value={data.s4_notes} onChange={(v) => set("s4_notes", v)} />
    </Box>
  );
}

function Tab5({ data, set, cb }: TabProps) {
  return (
    <Box sx={{ p: 2 }}>
      <SectionHeader title="Legal Considerations" purpose="Reduce risk and future problems." />

      <DiscussionBox
        prompts={[
          "Have you worked with an attorney for the business?",
          "Do you use contracts with clients or vendors?",
          "Any concerns about liability, Intellectual Property, or disputes?",
        ]}
      />

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Legal Checklist
      </Typography>
      <YesNo
        label="Client contracts in place?"
        value={data.s5_contracts}
        onChange={(v) => set("s5_contracts", v)}
      />
      <YesNo
        label="Terms & conditions / privacy policy (if online)?"
        value={data.s5_terms}
        onChange={(v) => set("s5_terms", v)}
      />
      <YesNo
        label="Insurance coverage reviewed recently?"
        value={data.s5_insurance}
        onChange={(v) => set("s5_insurance", v)}
      />

      <CheckboxGroup
        label="Legal Pillar Checklist — Clients should provide:"
        options={[
          "Any unsigned leases",
          "Any legal documents or agreements (drafts acceptable)",
          "Patent database checked if client is seeking a patent",
          "Current entity formation verified",
          "EIN filing completed",
        ]}
        values={data.s5_pillar}
        onChange={(v, c) => cb("s5_pillar", v, c)}
      />

      <NotesArea value={data.s5_notes} onChange={(v) => set("s5_notes", v)} />
    </Box>
  );
}

function Tab6({ data, set, cb }: TabProps) {
  return (
    <Box sx={{ p: 2 }}>
      <SectionHeader
        title="Information Technology (IT) & Systems"
        purpose="Assess operational efficiency and security."
      />

      <DiscussionBox
        prompts={[
          "What kind of tools do you rely on daily?",
          "Any tech frustrations or bottlenecks?",
          "How comfortable are you with current systems?",
        ]}
      />

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Systems Overview
      </Typography>
      <Stack spacing={1.5} sx={{ mb: 2 }}>
        <TextField
          label="Email platform"
          size="small"
          value={data.s6_email_platform}
          onChange={(e) => set("s6_email_platform", e.target.value)}
        />
        <TextField
          label="File storage"
          size="small"
          value={data.s6_file_storage}
          onChange={(e) => set("s6_file_storage", e.target.value)}
        />
      </Stack>
      <YesNo
        label="CRM or customer database?"
        value={data.s6_crm}
        onChange={(v) => set("s6_crm", v)}
      />
      <YesNo
        label="Cybersecurity measures?"
        value={data.s6_cybersecurity}
        onChange={(v) => set("s6_cybersecurity", v)}
      />
      <YesNo
        label="Data backed up regularly?"
        value={data.s6_data_backup}
        onChange={(v) => set("s6_data_backup", v)}
      />

      <CheckboxGroup
        label="IT Pillar Checklist — Client should have:"
        options={[
          "List of current systems/services used",
          "Cybersecurity provider info (if any)",
          'Specific issues/questions recorded (not vague) — e.g., "Need CRM that integrates email/SMS + inventory + online sales"',
        ]}
        values={data.s6_pillar}
        onChange={(v, c) => cb("s6_pillar", v, c)}
      />

      <NotesArea value={data.s6_notes} onChange={(v) => set("s6_notes", v)} />
    </Box>
  );
}

function Tab7({ data, set, cb }: TabProps) {
  return (
    <Box sx={{ p: 2 }}>
      <SectionHeader
        title="Business Consulting (Strategy, Operations & Growth)"
        purpose="Diagnose leadership clarity, business model, and scalability."
      />

      <DiscussionBox
        prompts={[
          "Do you follow a business plan?",
          "How often do you review or adjust your strategy?",
          "Which services/products are most profitable?",
          "Are there processes repeated manually that could be simplified?",
          "What processes are not yet self-sustaining without your direct involvement?",
          "What numbers do you look at to judge performance?",
          "Do you track customer behavior or sales trends?",
          "How confident are you in your data?",
          "What is your cost of goods sold?",
          "What are your sales margins?",
          "What is your breakeven point?",
        ]}
      />

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Data & Metrics
      </Typography>
      <Stack spacing={1.5} sx={{ mb: 2 }}>
        <TextField
          label="KPIs currently tracked"
          size="small"
          value={data.s7_kpis}
          onChange={(e) => set("s7_kpis", e.target.value)}
        />
        <TextField
          label="Sales reporting frequency"
          size="small"
          value={data.s7_sales_freq}
          onChange={(e) => set("s7_sales_freq", e.target.value)}
        />
      </Stack>
      <YesNo
        label="Marketing analytics reviewed?"
        value={data.s7_mktg_analytics}
        onChange={(v) => set("s7_mktg_analytics", v)}
      />
      <TextField
        label="Tools used (Google Analytics, POS reports, dashboards)"
        size="small"
        fullWidth
        value={data.s7_tools}
        onChange={(e) => set("s7_tools", e.target.value)}
        sx={{ mb: 1.5 }}
      />
      <TextField
        label="Data used for decisions or mostly intuition?"
        size="small"
        fullWidth
        value={data.s7_data_basis}
        onChange={(e) => set("s7_data_basis", e.target.value)}
        sx={{ mb: 2 }}
      />

      <CheckboxGroup
        label="Business Consulting Pillar Checklist — Client must have:"
        options={[
          "Stated intention/objective for consulting",
          "Mission/purpose statement for their business",
          "Preliminary Business Model Canvas",
          "Draft or first iteration of business plan",
          "Business goals identified",
          "Basic market validation completed",
          "Market research completed to some extent",
        ]}
        values={data.s7_pillar}
        onChange={(v, c) => cb("s7_pillar", v, c)}
      />

      <NotesArea value={data.s7_notes} onChange={(v) => set("s7_notes", v)} />
    </Box>
  );
}

function Tab8({ data, set, cb }: TabProps) {
  return (
    <Box sx={{ p: 2 }}>
      <SectionHeader
        title="Human Resources (HR)"
        purpose="Evaluate team structure, compliance, and workforce planning."
      />

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Operational Snapshot — Number of Employees (FT / PT / Contractors)
      </Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          label="Current Year"
          size="small"
          value={data.s8_emp_current}
          onChange={(e) => set("s8_emp_current", e.target.value)}
        />
        <TextField
          label="2025"
          size="small"
          value={data.s8_emp_2025}
          onChange={(e) => set("s8_emp_2025", e.target.value)}
        />
        <TextField
          label="2024"
          size="small"
          value={data.s8_emp_2024}
          onChange={(e) => set("s8_emp_2024", e.target.value)}
        />
      </Stack>

      <DiscussionBox
        prompts={[
          "Do you currently have employees or contractors or plan to hire soon?",
          "What roles exist today, and which are missing?",
          "Are responsibilities clearly defined?",
          "How do you communicate expectations with your team?",
          "Do you have documented policies or guidelines?",
          "Are you confident about labor laws and classification?",
          "Do you have any written agreements with team members?",
          "Are you protected legally as an employer?",
          "What would help you build a stronger team?",
        ]}
      />

      <CheckboxGroup
        label="HR Pillar Checklist — Client should have:"
        options={[
          "Employee handbook or draft",
          "Employee information list",
          "Draft job descriptions (if applicable)",
          "Draft onboarding process",
          "List of current employee trainings",
          "List of current policies & SOPs",
          "Payroll information/records",
        ]}
        values={data.s8_pillar}
        onChange={(v, c) => cb("s8_pillar", v, c)}
      />

      <NotesArea value={data.s8_notes} onChange={(v) => set("s8_notes", v)} />
    </Box>
  );
}

function Tab9({ data, set }: TabProps) {
  return (
    <Box sx={{ p: 2 }}>
      <SectionHeader
        title="Pain Points, Priorities & Opportunities"
        purpose="Align consulting work with what matters most."
      />

      <DiscussionBox
        prompts={[
          "What seems broken or inefficient right now?",
          "If you could fix one thing in 90 days, what would it be?",
          "Where do you think you're stuck?",
          "What would success look like in 6–12 months?",
        ]}
      />

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Priority Ranking
      </Typography>
      <Stack spacing={1.5} sx={{ mb: 2 }}>
        <TextField
          label="Priority 1"
          size="small"
          value={data.s9_priority_1}
          onChange={(e) => set("s9_priority_1", e.target.value)}
        />
        <TextField
          label="Priority 2"
          size="small"
          value={data.s9_priority_2}
          onChange={(e) => set("s9_priority_2", e.target.value)}
        />
        <TextField
          label="Priority 3"
          size="small"
          value={data.s9_priority_3}
          onChange={(e) => set("s9_priority_3", e.target.value)}
        />
      </Stack>

      <NotesArea value={data.s9_notes} onChange={(v) => set("s9_notes", v)} />
    </Box>
  );
}

function Tab10({ data, set, cb }: TabProps) {
  const observationFields: [keyof FormData, string][] = [
    ["s10_key_risks", "Key risks identified"],
    ["s10_strengths", "Strengths identified"],
    ["s10_immediate_gaps", "Immediate gaps"],
    ["s10_growth_opps", "Growth opportunities"],
  ];

  return (
    <Box sx={{ p: 2 }}>
      <SectionHeader
        title="Consultant Notes, Follow-Up & Next Steps"
        purpose="Turn assessment into clear action and accountability."
      />

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Consultant Observations
      </Typography>
      <Stack spacing={1.5} sx={{ mb: 2 }}>
        {observationFields.map(([key, label]) => (
          <TextField
            key={key as string}
            label={label}
            size="small"
            multiline
            minRows={2}
            fullWidth
            value={data[key] as string}
            onChange={(e) => set(key as string, e.target.value)}
          />
        ))}
      </Stack>

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Action Timeline
      </Typography>
      <Stack spacing={1.5} sx={{ mb: 2 }}>
        <TextField
          label="Quick Wins (0–90 days)"
          size="small"
          multiline
          minRows={2}
          fullWidth
          value={data.s10_quick_wins}
          onChange={(e) => set("s10_quick_wins", e.target.value)}
        />
        <TextField
          label="Mid-Term Opportunities (within a year)"
          size="small"
          multiline
          minRows={2}
          fullWidth
          value={data.s10_mid_term}
          onChange={(e) => set("s10_mid_term", e.target.value)}
        />
        <TextField
          label="Long-Term Strategy (over a year)"
          size="small"
          multiline
          minRows={2}
          fullWidth
          value={data.s10_long_term}
          onChange={(e) => set("s10_long_term", e.target.value)}
        />
      </Stack>

      <CheckboxGroup
        label="Recommended Support Areas"
        options={[
          "Business Consulting",
          "Marketing",
          "Legal",
          "Human Resources",
          "Information Technology",
          "Accounting/Finance",
        ]}
        values={data.s10_support_areas}
        onChange={(v, c) => cb("s10_support_areas", v, c)}
      />

      <TextField
        label="Client Deliverables (Before Next Meeting)"
        size="small"
        multiline
        minRows={2}
        fullWidth
        value={data.s10_client_deliverables}
        onChange={(e) => set("s10_client_deliverables", e.target.value)}
        sx={{ mb: 2 }}
      />

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Follow-Up
      </Typography>
      <Stack spacing={1.5} sx={{ mb: 2 }}>
        <TextField
          label="Next meeting date"
          size="small"
          value={data.s10_next_meeting}
          onChange={(e) => set("s10_next_meeting", e.target.value)}
        />
        <TextField
          label="Meeting objective"
          size="small"
          value={data.s10_meeting_obj}
          onChange={(e) => set("s10_meeting_obj", e.target.value)}
        />
        <TextField
          label="Assigned responsibilities (Consultant / Client)"
          size="small"
          value={data.s10_responsibilities}
          onChange={(e) => set("s10_responsibilities", e.target.value)}
        />
        <TextField
          label="Referrals to B2B providers"
          size="small"
          value={data.s10_referrals}
          onChange={(e) => set("s10_referrals", e.target.value)}
        />
      </Stack>

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        B2B Questions
      </Typography>
      <Stack spacing={1.5} sx={{ mb: 2 }}>
        <TextField
          label="What can we accomplish in the 5 hours?"
          size="small"
          value={data.s10_b2b_accomplish}
          onChange={(e) => set("s10_b2b_accomplish", e.target.value)}
        />
        <TextField
          label="How many estimated hours will this take (based on pillar)?"
          size="small"
          value={data.s10_b2b_hours}
          onChange={(e) => set("s10_b2b_hours", e.target.value)}
        />
      </Stack>

      <NotesArea value={data.s10_notes} onChange={(v) => set("s10_notes", v)} />
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const TAB_LABELS = [
  "1. Snapshot",
  "2. Identity",
  "3. Marketing",
  "4. Accounting",
  "5. Legal",
  "6. IT",
  "7. Consulting",
  "8. HR",
  "9. Pain Points",
  "10. Follow-Up",
];

const currentBNASurveyId = "6504ae6817baa329c061dee7";

// Fields counted for progress (excludes _notes fields)
export const TRACKED_FIELDS: (keyof FormData)[] = [
  "s1_stage",
  "s2_doc_checks", "s2_client_status", "s2_required_docs",
  "s3_target_audience", "s3_uvp", "s3_marketing_plan", "s3_channels",
  "s3_social_platforms", "s3_website_updated", "s3_brand_consistent", "s3_testimonials", "s3_pillar",
  "s4_rev_current", "s4_rev_2025", "s4_rev_2024", "s4_software",
  "s4_bookkeeper", "s4_separate_finances", "s4_cash_flow", "s4_pillar",
  "s5_contracts", "s5_terms", "s5_insurance", "s5_pillar",
  "s6_email_platform", "s6_file_storage", "s6_crm", "s6_cybersecurity", "s6_data_backup", "s6_pillar",
  "s7_kpis", "s7_sales_freq", "s7_mktg_analytics", "s7_tools", "s7_data_basis", "s7_pillar",
  "s8_emp_current", "s8_emp_2025", "s8_emp_2024", "s8_pillar",
  "s9_priority_1", "s9_priority_2", "s9_priority_3",
  "s10_key_risks", "s10_strengths", "s10_immediate_gaps", "s10_growth_opps", "s10_quick_wins",
  "s10_mid_term", "s10_long_term", "s10_support_areas", "s10_client_deliverables",
  "s10_next_meeting", "s10_meeting_obj", "s10_responsibilities",
  "s10_referrals", "s10_b2b_accomplish", "s10_b2b_hours",
];

// Which tracked fields belong to each tab (same order as TAB_LABELS)
const TAB_FIELDS: (keyof FormData)[][] = [
  ["s1_stage"],
  ["s2_doc_checks", "s2_client_status", "s2_required_docs"],
  ["s3_target_audience", "s3_uvp", "s3_marketing_plan", "s3_channels",
   "s3_social_platforms", "s3_website_updated", "s3_brand_consistent", "s3_testimonials", "s3_pillar"],
  ["s4_rev_current", "s4_rev_2025", "s4_rev_2024", "s4_software",
   "s4_bookkeeper", "s4_separate_finances", "s4_cash_flow", "s4_pillar"],
  ["s5_contracts", "s5_terms", "s5_insurance", "s5_pillar"],
  ["s6_email_platform", "s6_file_storage", "s6_crm", "s6_cybersecurity", "s6_data_backup", "s6_pillar"],
  ["s7_kpis", "s7_sales_freq", "s7_mktg_analytics", "s7_tools", "s7_data_basis", "s7_pillar"],
  ["s8_emp_current", "s8_emp_2025", "s8_emp_2024", "s8_pillar"],
  ["s9_priority_1", "s9_priority_2", "s9_priority_3"],
  ["s10_key_risks", "s10_strengths", "s10_immediate_gaps", "s10_growth_opps", "s10_quick_wins",
   "s10_mid_term", "s10_long_term", "s10_support_areas", "s10_client_deliverables",
   "s10_next_meeting", "s10_meeting_obj", "s10_responsibilities",
   "s10_referrals", "s10_b2b_accomplish", "s10_b2b_hours"],
];

const isFilled = (val: any): boolean =>
  Array.isArray(val) ? val.length > 0 : val !== "";

export const computeProgress = (data: FormData): { filled: number; total: number } => {
  const total = TRACKED_FIELDS.length;
  const filled = TRACKED_FIELDS.filter((f) => isFilled(data[f])).length;
  return { filled, total };
};

const BusinessNeedsAssessment = (props: any) => {
  const [activeTab, setActiveTab] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);
  useEffect(() => { contentRef.current?.scrollTo(0, 0); }, [activeTab]);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [originalData, setOriginalData] = useState<{
    survey_result: FormData;
    _id: string;
    survey_date: any;
  }>({ survey_result: initialFormData, _id: "", survey_date: dayjs() });
  const [surveyDate, setSurveyDate] = useState<Dayjs>(dayjs());

  // Autosave state
  const isLoaded = useRef(false);
  const skipNextAutosave = useRef(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState<"idle" | "saving" | "saved">("idle");
  const [draftFormData, setDraftFormData] = useState<FormData | null>(null);
  const [showDraftSnackbar, setShowDraftSnackbar] = useState(false);

  // Unfilled popover state
  const [unfillAnchor, setUnfillAnchor] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const existedSurveyID = getSurveyResultId(props.activity);
    const checkDraft = (baseline: FormData) => {
      axiosInstance
        .get(`/autosave?case_id=${props.case._id}&formType=BNA`)
        .then((r) => {
          if (r.data.draft) {
            const draft: FormData = { ...initialFormData, ...r.data.draft.formData };
            // Only offer restore if draft differs from what's already saved
            const hasDiff = Object.keys(findObjectDifferences(baseline, draft)).length > 0;
            if (hasDiff) {
              setDraftFormData(draft);
              setShowDraftSnackbar(true);
            }
          }
        })
        .catch(() => {});
    };

    if (existedSurveyID) {
      axiosInstance
        .get(`/survey/result?survey_result_id=${existedSurveyID}`)
        .then((res) => {
          if (res.status === 200) {
            const loaded: FormData = { ...initialFormData, ...res.data.survey_result };
            skipNextAutosave.current = true;
            setFormData(loaded);
            setOriginalData({
              survey_result: loaded,
              _id: res.data._id,
              survey_date: res.data.survey_date,
            });
            setSurveyDate(dayjs(res.data.survey_date));
            checkDraft(loaded);
          }
        })
        .catch(console.error);
    } else {
      checkDraft(initialFormData);
    }
    isLoaded.current = true;
  }, [props.activity]); // eslint-disable-line react-hooks/exhaustive-deps

  // Autosave debounce — fires 2 s after any formData change
  useEffect(() => {
    if (!isLoaded.current || skipNextAutosave.current) {
      skipNextAutosave.current = false;
      return;
    }
    setAutoSaveStatus("saving");
    const timer = setTimeout(() => {
      axiosInstance
        .post("/autosave", { case_id: props.case._id, formType: "BNA", formData })
        .then(() => setAutoSaveStatus("saved"))
        .catch(() => setAutoSaveStatus("idle"));
    }, 2000);
    return () => clearTimeout(timer);
  }, [formData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Notify parent of progress changes
  useEffect(() => {
    if (props.onProgressChange) {
      const { filled, total } = computeProgress(formData);
      props.onProgressChange(filled, total);
    }
  }, [formData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Per-tab unfilled counts
  const tabUnfilled = TAB_FIELDS.map(
    (fields) => fields.filter((f) => !isFilled(formData[f])).length
  );
  const totalUnfilled = tabUnfilled.reduce((a, b) => a + b, 0);

  const handleField = (field: string, value: any) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const handleCheckbox = (field: string, value: string, checked: boolean) => {
    setFormData((prev) => {
      const arr = [...(prev[field as keyof FormData] as string[])];
      if (checked) {
        if (!arr.includes(value)) arr.push(value);
      } else {
        const idx = arr.indexOf(value);
        if (idx > -1) arr.splice(idx, 1);
      }
      return { ...prev, [field]: arr };
    });
  };

  const handleSave = () => {
    const hasChanges =
      Object.keys(
        findObjectDifferences(
          { survey_result: originalData.survey_result, survey_date: originalData.survey_date },
          { survey_result: formData, survey_date: surveyDate }
        )
      ).length > 0;

    if (hasChanges) {
      axiosInstance
        .post(`/survey/result`, {
          activity_type: "Business Assessment",
          activity_date: surveyDate,
          surveyResult: {
            _id: originalData._id,
            survey_result: formData,
            survey_date: surveyDate,
            case_id: props.case._id,
            survey_id: currentBNASurveyId,
          },
        })
        .then((res) => {
          if (res.status === 200) {
            axiosInstance
              .delete(`/autosave?case_id=${props.case._id}&formType=BNA`)
              .catch(() => {});
            setAutoSaveStatus("idle");
            props.setCardOpen(false);
            props.setDataFlag(!props.dataFlag);
          }
        })
        .catch(console.error);
    } else {
      props.setCardOpen(false);
    }
  };

  const tabProps: TabProps = { data: formData, set: handleField, cb: handleCheckbox };

  const tabPanels = [
    <Tab1 {...tabProps} />,
    <Tab2 {...tabProps} />,
    <Tab3 {...tabProps} />,
    <Tab4 {...tabProps} />,
    <Tab5 {...tabProps} />,
    <Tab6 {...tabProps} />,
    <Tab7 {...tabProps} />,
    <Tab8 {...tabProps} />,
    <Tab9 {...tabProps} />,
    <Tab10 {...tabProps} />,
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Header */}
      <Box
        sx={{
          textAlign: "center",
          backgroundColor: BRAND,
          padding: "3px",
          color: "white",
          borderRadius: "5px 5px 0 0",
          mb: "16px",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1,
        }}
      >
        <Box sx={{ width: 80 }} />
        <Typography variant="h4">Business Needs Assessment</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: 80, justifyContent: "flex-end" }}>
          {totalUnfilled > 0 && (
            <Chip
              icon={<WarningAmberIcon sx={{ fontSize: "0.85rem !important", color: "#fff !important" }} />}
              label={`${totalUnfilled} unfilled`}
              size="small"
              onClick={(e) => setUnfillAnchor(e.currentTarget)}
              sx={{
                bgcolor: "rgba(255,255,255,0.25)",
                color: "#fff",
                fontSize: "0.7rem",
                cursor: "pointer",
                "&:hover": { bgcolor: "rgba(255,255,255,0.4)" },
              }}
            />
          )}
          <CloseIcon sx={{ fontSize: 40, cursor: "pointer" }} onClick={() => props.setCardOpen(false)} />
        </Box>
      </Box>

      {/* Unfilled tabs popover */}
      <Popover
        open={Boolean(unfillAnchor)}
        anchorEl={unfillAnchor}
        onClose={() => setUnfillAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Typography variant="caption" sx={{ px: 2, pt: 1, display: "block", color: "text.secondary" }}>
          Tabs with unfilled fields
        </Typography>
        <List dense disablePadding>
          {TAB_LABELS.map((label, i) =>
            tabUnfilled[i] > 0 ? (
              <ListItemButton
                key={label}
                onClick={() => { setActiveTab(i); setUnfillAnchor(null); }}
                sx={{ gap: 1 }}
              >
                <Typography variant="body2" sx={{ flex: 1 }}>{label}</Typography>
                <Typography variant="caption" sx={{ color: "warning.main", fontWeight: 600 }}>
                  {tabUnfilled[i]} unfilled
                </Typography>
              </ListItemButton>
            ) : null
          )}
        </List>
      </Popover>

      {/* Date picker */}
      <Box sx={{ px: 2, flexShrink: 0 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DemoContainer components={["DatePicker"]} sx={{ mb: 1 }}>
            <DatePicker
              label="Assessment Date"
              value={surveyDate}
              onChange={(v) => {
                if (v) setSurveyDate(v);
              }}
            />
          </DemoContainer>
        </LocalizationProvider>
      </Box>

      {/* Tabs + content */}
      <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          orientation="vertical"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderRight: 1,
            borderColor: "divider",
            flexShrink: 0,
            width: 140,
            "& .MuiTab-root": { fontSize: "0.75rem", minWidth: 0, py: 1, px: 1.5, alignItems: "flex-start", textAlign: "left" },
            "& .Mui-selected": { color: `${BRAND} !important` },
            "& .MuiTabs-indicator": { backgroundColor: BRAND },
          }}
        >
          {TAB_LABELS.map((label, i) => (
            <Tab
              key={label}
              label={
                <Badge
                  variant="dot"
                  invisible={tabUnfilled[i] === 0}
                  sx={{ "& .MuiBadge-dot": { bgcolor: "#f59e0b", right: -4, top: 2 } }}
                >
                  {label}
                </Badge>
              }
            />
          ))}
        </Tabs>

        {/* Tab content */}
        <Box ref={contentRef} sx={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
          {tabPanels[activeTab]}
        </Box>
      </Box>

      {/* Save / Next */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderTop: "1px solid #e0e0e0",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 1,
          flexShrink: 0,
        }}
      >
        {autoSaveStatus === "saving" && (
          <Typography variant="caption" sx={{ color: "text.secondary", mr: 1 }}>Saving draft…</Typography>
        )}
        {autoSaveStatus === "saved" && (
          <Typography variant="caption" sx={{ color: "success.main", mr: 1 }}>Draft saved</Typography>
        )}
        <Button
          variant="contained"
          onClick={handleSave}
          sx={{ bgcolor: BRAND, "&:hover": { bgcolor: "#7a1850" } }}
        >
          Save
        </Button>
        {activeTab < TAB_LABELS.length - 1 && (
          <Button
            variant="outlined"
            onClick={() => setActiveTab((t) => t + 1)}
            sx={{ borderColor: BRAND, color: BRAND, "&:hover": { borderColor: "#7a1850", color: "#7a1850" } }}
          >
            Next
          </Button>
        )}
      </Box>

      {/* Draft restore snackbar */}
      <Snackbar
        open={showDraftSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="info"
          onClose={() => setShowDraftSnackbar(false)}
          action={
            <Button
              size="small"
              color="inherit"
              onClick={() => {
                if (draftFormData) {
                  skipNextAutosave.current = true;
                  setFormData(draftFormData);
                }
                setShowDraftSnackbar(false);
              }}
            >
              Restore
            </Button>
          }
        >
          Unsaved draft found
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BusinessNeedsAssessment;
